import * as DialogPrimitive from '@radix-ui/react-dialog'
import type { ReactNode, RefObject } from 'react'
import { Dialog, DialogPortal, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  children: ReactNode
  /** Scrollable body region — parent can scroll to top when switching steps. */
  bodyRef?: RefObject<HTMLDivElement | null>
}

/**
 * Slide-over cart panel — **non-modal**: no dimming overlay, so staff can keep using Make sales
 * (search, add lines) while the cart is open. `modal={false}` avoids focus trapping on the rest of the page.
 */
export function PosCartDrawer({ open, onOpenChange, title, description, children, bodyRef }: Props) {
  return (
    <Dialog modal={false} open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onInteractOutside={(e) => e.preventDefault()}
          className={cn(
            // Opaque surface: theme `--card` is translucent; use `--background` so lines behind don't show through.
            // Start below the dashboard sticky header (h-16) so the cart doesn't cover the navbar.
            'bg-background text-foreground border-border fixed top-16 right-0 z-30 flex h-[calc(100dvh-4rem)] max-h-[calc(100dvh-4rem)] w-full max-w-md flex-col border-l shadow-[var(--shadow-modal)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            'duration-200 motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none',
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-4 pr-12">
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold leading-tight">{title}</DialogTitle>
              {description ? (
                <p className="text-muted-foreground mt-1 text-sm leading-snug">{description}</p>
              ) : null}
            </div>
            <DialogPrimitive.Close
              type="button"
              className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-lg p-2 text-lg leading-none opacity-80 transition hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
            >
              <span aria-hidden>×</span>
              <span className="sr-only">Close cart</span>
            </DialogPrimitive.Close>
          </div>
          <div
            ref={bodyRef}
            className="flex min-h-0 flex-1 flex-col overflow-hidden overscroll-contain px-4 py-4"
          >
            {children}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
